import { useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { REAL_UNITS_PER_AU, modelRadial } from "@solar/ephemeris";
import { resolveQuality, useScene } from "../store";
import { frame } from "./frame";
import { softPointTexture } from "./materials";

const SQRT = Math.sqrt;
interface Belt { id: "belt" | "kuiper"; count: Record<"low" | "medium" | "high", number>; aMin: number; aMax: number; incMax: number; color: string; size: number; opacity: number }
const BELTS: Belt[] = [
  { id: "belt", count: { low: 900, medium: 1800, high: 3200 }, aMin: 2.15, aMax: 3.3, incMax: 0.22, color: "#c9bda9", size: 1.4, opacity: 0.75 },
  { id: "kuiper", count: { low: 700, medium: 1400, high: 2600 }, aMin: 39, aMax: 48, incMax: 0.3, color: "#8fb0d8", size: 1.2, opacity: 0.55 },
];

/**
 * 小行星带与柯伊伯带：示意粒子（不是真实小行星的位置），按开普勒周期绕太阳运动，
 * 与天体共用同一套模式插值与浮动原点。每颗粒子的径向压缩系数是常数，只在这里预先算好。
 */
export function ParticleBelt({ belt }: { belt: Belt["id"] }) {
  const cfg = BELTS.find((b) => b.id === belt)!;
  const hidden = useScene((s) => s.hidden.has(belt));
  const q = resolveQuality(useScene((s) => s.quality));
  const n = cfg.count[q];
  const data = useMemo(() => {
    let s = belt === "belt" ? 31 : 77; const rnd = () => (s = (s * 16807) % 2147483647) / 2147483647;
    const a = new Float32Array(n), ph = new Float32Array(n), w = new Float32Array(n), kM = new Float32Array(n);
    const u1 = new Float32Array(n * 3), u2 = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) {
      const ai = cfg.aMin + (cfg.aMax - cfg.aMin) * rnd();
      a[i] = ai; ph[i] = rnd() * Math.PI * 2; w[i] = (Math.PI * 2) / (365.25 * Math.pow(ai, 1.5));
      kM[i] = modelRadial(ai) / ai;
      // 轨道面：随机升交点 + 小倾角（正态近似）
      const inc = (rnd() + rnd() + rnd() - 1.5) * cfg.incMax, node = rnd() * Math.PI * 2;
      const cn = Math.cos(node), sn = Math.sin(node), ci = Math.cos(inc), si = Math.sin(inc);
      // 黄道 xyz；u1 沿升交点方向，u2 在轨道面内垂直于它
      u1[i * 3] = cn; u1[i * 3 + 1] = sn; u1[i * 3 + 2] = 0;
      u2[i * 3] = -sn * ci; u2[i * 3 + 1] = cn * ci; u2[i * 3 + 2] = si;
    }
    return { a, ph, w, kM, u1, u2 };
  }, [belt, n, cfg]);
  const geo = useMemo(() => { const g = new THREE.BufferGeometry(); g.setAttribute("position", new THREE.BufferAttribute(new Float32Array(n * 3), 3)); return g; }, [n]);

  useFrame(() => {
    if (hidden) return;
    const s = useScene.getState(), days = s.timeMs / 86400000, m = frame.m, o = frame.origin;
    const pos = geo.attributes.position as THREE.BufferAttribute, arr = pos.array as Float32Array;
    const { a, ph, w, kM, u1, u2 } = data;
    for (let i = 0; i < n; i++) {
      const th = ph[i]! + w[i]! * days, c = Math.cos(th) * a[i]!, sn = Math.sin(th) * a[i]!;
      // 黄道坐标 (x,y,z) → 场景 (x, z, −y)
      const ex = c * u1[i * 3]! + sn * u2[i * 3]!, ey = c * u1[i * 3 + 1]! + sn * u2[i * 3 + 1]!, ez = c * u1[i * 3 + 2]! + sn * u2[i * 3 + 2]!;
      const k = (1 - m) * kM[i]! + m * REAL_UNITS_PER_AU;
      arr[i * 3] = ex * k - o.x; arr[i * 3 + 1] = ez * k - o.y; arr[i * 3 + 2] = -ey * k - o.z;
    }
    pos.needsUpdate = true; geo.boundingSphere = null;
  });
  void SQRT;
  if (hidden) return null;
  return (
    <points geometry={geo} raycast={() => null} frustumCulled={false}>
      <pointsMaterial map={softPointTexture()} color={cfg.color} size={cfg.size * 2.4} sizeAttenuation={false} transparent opacity={cfg.opacity} depthWrite={false} />
    </points>
  );
}
